import { odooApi } from '../index';
import { Tender, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { extractErrorMessage } from '../transforms';
import DOMPurify from 'dompurify';

/**
 * Tenders API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all tender-related API endpoints.
 * Connected to real Odoo API at /api/moys/tenders
 */

/**
 * Map backend status to frontend status
 */
function mapTenderStatus(backendStatus: string): Tender['status'] {
  const statusMap: Record<string, Tender['status']> = {
    'draft': 'announced',
    'open': 'submission',
    'closed': 'awarded',
    'cancelled': 'cancelled',
  };
  return statusMap[backendStatus] || 'announced';
}

export const tendersApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get tenders from Odoo with pagination and filters
    getTenders: builder.query<
      PaginatedResponse<Tender>,
      { 
        page?: number; 
        pageSize?: number; 
        lang?: 'ar' | 'en' | 'ku'; 
        status?: string;
        dateFrom?: string;
        dateTo?: string;
        companyId?: number | string;
        search?: string;
      }
    >({
      query: ({ page = 1, pageSize = 10, lang = 'ar', status, dateFrom, dateTo, companyId, search }) => {
        const odooLang = getOdooLang(lang);
        
        // Backend uses GET method, but we'll use POST for consistency
        // Backend expects page and page_size via _get_pagination_params
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });

        // Add status filter if provided (backend expects: draft, open, closed, cancelled)
        if (status) {
          // Map frontend status to backend status if needed
          const backendStatusMap: Record<string, string> = {
            'announced': 'draft',
            'submission': 'open',
            'awarded': 'closed',
            'cancelled': 'cancelled',
          };
          const backendStatus = backendStatusMap[status] || status;
          params.append('status', backendStatus);
        }

        // Wire date range filters
        if (dateFrom) {
          params.append('date_from', dateFrom);
        }
        if (dateTo) {
          params.append('date_to', dateTo);
        }

        // Wire company filter
        if (companyId) {
          params.append('company_id', String(companyId));
        }

        // Wire search filter
        if (search) {
          params.append('search', search);
        }

        return {
          url: `/api/moys/tenders?${params.toString()}`,
          method: 'POST', // Backend supports POST for tenders
        };
      },
      transformResponse: (response: any, meta, arg) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }

          const tenders = response.data.map((item: any) => {
            // Sanitize description HTML
            const description = item.description || '';
            const safeDescription = DOMPurify.sanitize(description);

            // Map attachments from API response
            const attachments = (item.attachments || []).map((att: any) => ({
              id: String(att.id),
              name: att.name || '',
              url: att.file_url || '',
              size: att.file_size || 0,
              type: att.mimetype || '',
            }));

            return {
              id: String(item.id),
              titleAr: item.name || '',
              titleEn: item.name || '',
              titleKu: item.name || '',
              refNo: `TND-${String(item.id).padStart(6, '0')}`, // Generate refNo from ID
              status: mapTenderStatus(item.status || 'open'),
              publishDate: item.start_date || new Date().toISOString(),
              closeDate: item.end_date || new Date().toISOString(),
              attachments: attachments,
              departmentId: '', // Backend doesn't return department
              descriptionRich: {
                ar: safeDescription,
                en: safeDescription,
                ku: safeDescription,
              },
              budget: item.budget || 0,
              currency: item.currency || 'IQD',
              registrations: 0, // Backend doesn't have this field
              url: item.url || undefined, // Registration URL
              createdAt: item.start_date || new Date().toISOString(),
              updatedAt: item.end_date || new Date().toISOString(),
            };
          }) as Tender[];

          const pagination = response.pagination || {};
          const totalRecords =
            pagination.total_records ??
            pagination.totalRecords ??
            response.total_records ??
            tenders.length;
          const pageSize =
            pagination.page_size ?? pagination.pageSize ?? arg.pageSize ?? tenders.length;
          const currentPage =
            pagination.current_page ?? pagination.currentPage ?? arg.page ?? 1;
          const totalPages =
            pagination.total_pages ??
            pagination.totalPages ??
            Math.max(1, Math.ceil(totalRecords / pageSize));

          return {
            data: tenders,
            total: totalRecords,
            page: currentPage,
            pageSize,
            totalPages,
          } as PaginatedResponse<Tender>;
        } catch (error) {
          console.error('[Tenders API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Tender' as const, id })),
              { type: 'Tender', id: 'LIST' },
            ]
          : [{ type: 'Tender', id: 'LIST' }],
    }),

    // Get single tender by ID
    getTender: builder.query<Tender, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, api, extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);
        
        // Fetch tenders in batches to find the one we need
        let allTenders: Tender[] = [];
        let offset = 0;
        const limit = 100;
        let hasMore = true;
        
        while (hasMore && allTenders.length < 500) {
          const result = await baseQuery({
            url: `/api/moys/tenders?lang=${odooLang}&page=${Math.floor(offset / limit) + 1}&page_size=${limit}`,
            method: 'POST', // Backend supports POST for tenders
          });
          
          if (result.error) {
            if (offset === 0) {
              return { error: result.error as any };
            }
            break;
          }
          
          try {
            const response = result.data as any;
            
            if (!response.success || !response.data) {
              if (offset === 0) {
                return { error: { status: 500, data: 'Invalid response format' } as any };
              }
              break;
            }
            
            const tenders = response.data.map((item: any) => {
              const description = item.description || '';
              const safeDescription = DOMPurify.sanitize(description);
              
              // Map attachments from API response
              const attachments = (item.attachments || []).map((att: any) => ({
                id: String(att.id),
                name: att.name || '',
                url: att.file_url || '',
                size: att.file_size || 0,
                type: att.mimetype || '',
              }));
              
              return {
                id: String(item.id),
                titleAr: item.name || '',
                titleEn: item.name || '',
                titleKu: item.name || '',
                refNo: `TND-${String(item.id).padStart(6, '0')}`,
                status: mapTenderStatus(item.status || 'open'),
                publishDate: item.start_date || new Date().toISOString(),
                closeDate: item.end_date || new Date().toISOString(),
                attachments: attachments,
                departmentId: '',
                descriptionRich: {
                  ar: safeDescription,
                  en: safeDescription,
                  ku: safeDescription,
                },
                budget: item.budget || 0,
                currency: item.currency || 'IQD',
                registrations: 0,
                url: item.url || undefined, // Registration URL
                createdAt: item.start_date || new Date().toISOString(),
                updatedAt: item.end_date || new Date().toISOString(),
              };
            });
            
            allTenders = [...allTenders, ...tenders];
            
            // Check if we've fetched all tenders
            const pagination = response.pagination || {};
            const total = pagination.total_records || pagination.totalRecords || response.total_records || 0;
            if (tenders.length < limit || allTenders.length >= total) {
              hasMore = false;
            } else {
              offset += limit;
            }
          } catch (error) {
            console.error('[Tenders API] Transform error in getTender:', error);
            break;
          }
        }
        
        // Search for the tender by ID
        const tender = allTenders.find((t) => t.id === id || t.refNo === id);
        
        if (tender) {
          return { data: tender as Tender };
        }
        
        console.warn('[Tenders API] Tender not found:', { id, searchedTenders: allTenders.length });
        return { error: { status: 404, data: 'Tender not found' } as any };
      },
      providesTags: (result, error, { id }) => [{ type: 'Tender', id }],
    }),
  }),
});

export const {
  useGetTendersQuery,
  useGetTenderQuery,
} = tendersApi;

